import { PaginatedSearchQueryParams } from "@/store/types";
import { NotificationOptions } from "vue-notification";

export interface DevicesState {
	cancelTokenSource: any;
	doors: DeviceDetails[];
	serversList: ServerDetails[];
	filteredServersList: ServerDetails[];
	currentServer: ServerDetails;
	devices: DeviceDetails[];
	device: DeviceDetails;
	deviceChangeErrors: DeviceChanges;
	currentDeviceTypeIdentifier: DeviceTypeIdentifier;
	serverTypes: ServerType[];
	recordsRetrievalStatus: DeviceRecordsRetrievalStatus;
	deviceConfigurationErrors: DeviceConfigurationErrors;
	deviceResponseRecordsRetrievalStatus: DeviceResponseRecordsRetrievalStatus;
	deviceTypeCounts?: DeviceTypeCounts;
	alarms?: number;
	deleteChildDeviceModalVisible: boolean;
	editChildDeviceModalVisible: boolean;
	cameraPreviewModalVisible: boolean;
	notificationOptions: NotificationOptions | null;
	serverLineProfiles: ServerLineProfile[];
	serverConfig: DeviceConfiguration[];
	serverDeviceChanges: DeviceChanges;
	gettingConfig: boolean;
	settingConfig: boolean;
	serverModalOpened: boolean;
}

export interface DeviceDetails {
	deviceID: number;
	serverID: number;
	deviceTypeID: number;
	title: string;
	quality: string;
	speed: string;
	hasPtz: boolean;
	auditServiceEndpoint: string;
	deviceServerEndpoint: string;
	eventRecordId: number | null;
	timeZone?: string | null;
	presets: DevicePreset[];
	input1: number;
	input2: number;
	serverTitle?: string | null | undefined;
	extraValue: string;
	settingsMeta: string;
	audioSettings?: AudioSettingsMeta;
	cameraSettings?: CameraSettingsMeta;
	relaySettings?: RelaySettingsMeta;
	defaultReferenceShot?: string;
	groupID?: number;
	deviceServiceSession?: string;
}

export interface DevicePreset {
	presetNumber: number;
	title: string;
}

export interface ServerDetails {
	serverID: number;
	serverTypeID: number;
	title: string;
	groupID: number;
	host: string;
	port: number;
	username: string;
	password: string;
	extraValue: string;
	groupName?: string;
	extraValueName?: string;
	devices?: any[];
	isRaiseIndividual?: boolean;
	isRaiseGrouped?: boolean;
	applianceId?: number;
	groupSyncId?: number | null;
	receiveUndefinedAlarms: boolean;
	logDisarmedAlarms: boolean;
	showEdge: boolean;
	edgePath: string;
	edgeUser: string;
	edgePassword: string;
	deviceTypeCounts?: DeviceTypeCounts;
	alarms?: number;
	recordsRetrievalStatus?: DeviceRecordsRetrievalStatus;
	deviceResponseRecordsRetrievalStatus?: DeviceResponseRecordsRetrievalStatus;
	accountNumber: string;
	serverLineProfileId?: number;
}

export interface ServerConfiguration {
    serverId: number;
	extraValue: string;
	deviceConfiguration: DeviceConfiguration;
}

export interface ServerConfigurationResponse {
    serverConfiguration: ServerConfiguration,
	successfulUpdates: DeviceConfigurationUpdates,
	errors: DeviceConfigurationErrors;
}

export interface DeviceConfigurationUpdates {
	alarms: Response[];
	cameras: DeviceDetails[];
	outputs: DeviceDetails[];
	doors: DeviceDetails[];
	audios: DeviceDetails[];
}

export interface DeviceConfigurationErrors {
	alarms: DeviceConfigurationAlarm[];
	cameras: DeviceConfigurationCamera[];
	outputs: DeviceConfigurationRelay[];
	doors: DeviceConfigurationRelay[];
	audios: DeviceConfigurationAudio[];
}

export interface DeviceChange {
	deviceId: number;
	deviceTitle: string;
	details: string;
	include: boolean;
	changeType: ChangeType;
	cameraConfiguration: DeviceConfigurationCamera;
	alarmConfiguration: DeviceConfigurationAlarm;
	audioConfiguration: DeviceConfigurationAudio;
	relayConfiguration: DeviceConfigurationRelay;
	updateType: UpdateType;
	changeTypeTitle: string;
	changeDevice: ChangeDevice;
	updatedOrAddedDevice: DeviceDetails | null;
	updatedOrAddedResponse: Response | null;
	selected: boolean;
	deviceType: number;
}

export interface DeviceChanges {
	serverId: number;
	serverChanges: DeviceChange[];
	cameraChanges: DeviceChange[];
	alarmChanges: DeviceChange[];
	audioChanges: DeviceChange[];
	outputChanges: DeviceChange[];
	doorChanges: DeviceChange[];
}

export interface DeviceChangesResponse {
	changes: DeviceChanges;
	successfulChanges: DeviceChanges;
	errors: DeviceChanges;
}

export interface ChangeDevice {
	input1Changed: boolean;
	input1: number | null;
	titleChanged: boolean;
	title: string | null;
	extraValueChanged: boolean;
	extraValue: string | null;
	hasPtzChanged: boolean;
	haspPtz: boolean | null;
	input2Changed: boolean;
	input2: number | null;
	canRx: boolean | null;
	canRxChanged: boolean;
	canTx: boolean | null;
	canTxChanged: boolean;
}

export enum ChangeType {
	Cameras,
    Camera_Presets,
    Relay,
    Audio,
    Alarm,
    ServerDetail
}

export enum UpdateType {
	Add,
    Update,
    Delete
}

export interface DeviceConfiguration {
	name: string;
	extraValue: string;
	host: string;
	port: number;
	username: string;
	password: string;
	syncIdentifier: string;
	alarms: DeviceConfigurationAlarm[];
	cameras: DeviceConfigurationCamera[];
	relays: DeviceConfigurationRelay[];
	audios: DeviceConfigurationAudio[];
	users: DeviceConfigurationUser[];
}

export interface DeviceConfigurationSelections {
	name: string;
	extraValue: string;
	host: string;
	port: number;
	username: string;
	password: string;
	syncIdentifier: string;
	alarms: DeviceConfigurationAlarmSelection[];
	cameras: DeviceConfigurationCameraSelection[];
	doors: DeviceConfigurationRelaySelection[];
	outputs: DeviceConfigurationRelaySelection[];
	audios: DeviceConfigurationAudioSelection[];
	users: DeviceConfigurationUser[];
}

export interface DeviceConfigurationAlarmSelection extends DeviceConfigurationAlarm {
	selected: boolean
}
export interface DeviceConfigurationAlarm {
	title: string;
	input1: number;
	input2: number;
	eventType: number;
	syncIdentifier: string;
	extraValue: string;
	linkedCameraInput: number;
	highPriority: boolean;
}

export interface DeviceConfigurationCameraSelection extends DeviceConfigurationCamera {
	selected: boolean
}

export interface DeviceConfigurationCamera {
	name: string;
    input1: number;
    hasPtz: boolean;
    presets: DeviceConfigurationPreset[];
    referenceImage: number[];
    syncIdentifier: string;
	extraValue: string;
    features: DeviceFeature[];
	latLong: string;
}

export interface DeviceConfigurationRelaySelection extends DeviceConfigurationRelay {
	selected: boolean
}

export interface DeviceConfigurationRelay {
	name: string;
    input1: number;
	input2: number;
    type: number;
    syncIdentifier: string;
	extraValue: string;
    features: DeviceFeature[];
	latLong: string;
}

export interface DeviceConfigurationAudioSelection extends DeviceConfigurationAudio {
	selected: boolean
}

export interface DeviceConfigurationAudio {
	name: string;
    input1: number;
    type: number;
    syncIdentifier: string;
	extraValue: string;
    features: DeviceFeature[];
	latLong: string;
}

export interface DeviceConfigurationUser {
	name: string;
	address: string;
	email: string;
	phone: string;
	extraValue: string;
	externalID: string;
	serverID: number;
}

export interface DeviceFeature {
	name: string;
	value: string;
}

export interface DeviceConfigurationPreset {
	name: string;
	position: number;
}

export interface DeviceConfigurationPresetTableItems extends DeviceConfigurationPreset {
	editing: boolean;
	creating: boolean;
}

export interface DeviceConfigurationPresetPayload extends DeviceConfigurationPreset {
	deviceId: number,
	groupId: number
}

export interface DeviceSearchResult {
	deviceId: number;
	deviceTypeId: number;
	groupId: number;
	settingsMeta: string;
	title: string;
	serverTypeId?: number;
}

export interface DeviceSearchQuery {
	query: string;
	deviceTypeId: number;
	searchTermsInAnyOrder?: boolean;
}

export interface DeviceSearchSwipeAndShowQuery {
	query: string;
	searchTermsInAnyOrder?: boolean;
	regionUrl?: string;
}

export interface FetchServerByGroupParams {
	groupId: number;
	pageSize?: number;
}

export interface AddDevicesForServerParams {
	serverId: number;
	devicesLoaded: DeviceConfigurationSelections
	devices: DeviceConfiguration;
}

export interface FetchConfigForServerParams {
	server: ServerDetails;
	serverType: ServerType;
	auth: string;
}

export interface SearchServerByGroupParams extends FetchServerByGroupParams {
	filter: string;
}

export interface GetDevicesByTypeParams {
	deviceTypeId: number;
	serverId?: number | null | undefined;
	page: number;
	pageSize: number;
	searchTerm?: string | null | undefined;
	selectOutputs?: boolean | null | undefined;
	includeReferenceShots?: boolean | null | undefined;
}

export interface GetDeviceForGroupByTypeParams {
	groupId: number;
	deviceTypeId: number;
	filter: string | null | undefined;
}

export interface GetDeviceParams {
	eventId: number;
	deviceAction: string;
	noRecordingCamera: boolean;
}

export enum DeviceType {
	Camera = 1,
	Audio = 2,
	Relay = 3,
	SplitCamera = 4
}

export enum DeviceTypeIdentifier {
	Camera = 1,
	Audio = 2,
	Output = 3,
	Door = 4
}

export interface ServerType {
	serverTypeID: number;
	title: string;
	canSendMessage: boolean;
	canNotifyRadio: boolean;
	extraValueName: string;
	usesDeviceExtraValue: boolean;
	usesExtraCameraIds: boolean;
	usesExtraRelayIds: boolean;
	usesExtraAudioIds: boolean;
	numAudioInputs: number;
	numCameras: number;
	numRelays: number;
	numAlarmInputs: number;
	hasMatrixBuiltIn: boolean;
	canSync: boolean;
	canSyncDynamicDataList: boolean;
	isAlarmPanel: boolean;
	dll: string;
	dllDeviceType: string;
	urlTemplate: string;
	canGetConfig: boolean;
	hidden: boolean;
}

export interface DeviceTypeCounts {
	devices: number;
	cameras: number;
	audios: number;
	doors: number;
	outputs: number;
	alarms: number;
}

export interface DeviceResponseRecordsRetrievalStatus extends DeviceRecordsRetrievalStatus {
	allAlarmsRetrieved: boolean;
}

export interface DeviceRecordsRetrievalStatus {
	allServersRetrieved: boolean;
	allFilteredServersRetrieved: boolean;
	allCamerasRetrieved: boolean;
	allFilteredCamerasRetrieved: boolean;
	allAudiosRetrieved: boolean;
	allFilteredAudiosRetrieved: boolean;
	allDoorsRetrieved: boolean;
	allFilteredDoorsRetrieved: boolean;
	allOutputsRetrieved: boolean;
	allFilteredOutputsRetrieved: boolean;
}

export interface ServersWithEnabledDeviceTypesParams extends PaginatedSearchQueryParams {
	audiosEnabled?: boolean;
	camerasEnabled?: boolean;
	relaysEnabled?: boolean;
}

export interface AudioSettingsMeta {
	transmitEnabled: boolean;
	receiveEnabled: boolean;
}

export interface CameraSettingsMeta {
	qual: string;
	telemServerId: number;
	telemSpeed: string;
}

export interface RelaySettingsMeta {
	relayType?: string;
	onOffEnabled: boolean;
	pulseEnabled: boolean;
	stateSwitchEnabled: boolean;
	stateSwitchDefaultState: boolean;
}

export interface ServerLineProfile {
	serverLineProfileId: number;
	title: string;
	groupId?: number;
}

export interface CameraReferenceShot {
	DeviceID: number;
	Image: string;
}
